import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Copy, Download, Plus, KeyRound } from "lucide-react";
import api from "../../lib/api";
import { Modal } from "../../components/Modal";
import { formatApiErrorDetail } from "../../lib/format";

const CONNECT_TEXT = `CONNECT YOUR TOOLS
Base URL: https://workingactor.studio/api
Put your key in this header on every request:
Authorization: Bearer wa_YOUR_KEY
(If a tool reserves the Authorization header for something else, send the same key as X-API-Key: wa_YOUR_KEY instead.)
Keys go in a header only, never in a link or URL.
Terminal, scripts and coding assistants:
curl -H "Authorization: Bearer wa_YOUR_KEY" https://workingactor.studio/api/admin/signups
Everything the API can do: https://workingactor.studio/api/admin/openapi.json (same header).
ChatGPT (Custom GPT → Actions):
1. In the API reference below, click "Copy full schema JSON".
2. In the Action editor, paste the schema (don't use "Import from URL" — the schema itself needs your key to view).
3. Authentication → API Key → Auth Type: Bearer → paste your key.
Chat apps that add tools as "connectors" (MCP): not supported yet. Ask us and we'll look at adding one.
To hand this to an assistant, paste:
"You can use the Working Actor studio API. Base URL: https://workingactor.studio/api. Send the header Authorization: Bearer <my key> on every request. The full OpenAPI spec is at https://workingactor.studio/api/admin/openapi.json (same header). Never put the key in a URL. Ask me before you delete, archive, or change anything."
Keep your key safe: it has full admin rights (unless you made it read-only). Treat it like a password — never paste it into a public chat, a shared document, or code you share. If it's ever exposed, revoke it here and create a new one.`;

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return String(iso);
  }
}

async function copyText(text, label) {
  await navigator.clipboard.writeText(text);
  toast.success(`${label} copied`);
}

export default function ReefApiAccess() {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [readOnly, setReadOnly] = useState(false);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null); // {name, prefix, key} — lives only here, cleared on close
  const [revoking, setRevoking] = useState(null);
  const [spec, setSpec] = useState(null);
  const [audit, setAudit] = useState([]);

  const load = useCallback(async () => {
    const [k, a] = await Promise.all([api.get("/admin/api-keys"), api.get("/admin/audit-log?limit=50")]);
    setKeys(k.data);
    setAudit(a.data);
  }, []);

  useEffect(() => {
    load().catch(() => {});
    api.get("/admin/openapi.json").then((r) => setSpec(r.data)).catch(() => {});
  }, [load]);

  const createKey = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/admin/api-keys", { name, read_only: readOnly });
      setCreated(r.data);
      setName("");
      setReadOnly(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    try {
      await api.delete(`/admin/api-keys/${revoking.id}`);
      toast.success("Key revoked — it stops working immediately.");
      setRevoking(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const downloadSpec = () => {
    const blob = new Blob([JSON.stringify(spec, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "workingactor-api.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const serverUrl = spec?.servers?.[0]?.url || "";
  const endpoints = spec
    ? Object.entries(spec.paths || {}).flatMap(([path, ops]) =>
        Object.entries(ops).map(([method, op]) => ({ method: method.toUpperCase(), path, summary: op.summary || "" })))
    : [];

  return (
    <div className="space-y-10" data-testid="reef-api-access">
      <div>
        <p className="overline mb-2">Connect your own software</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight">API access</h1>
        <p className="font-sans text-sm text-stone mt-2 max-w-xl">
          Personal keys with your admin rights. You see a key once, when you create it; revoke it anytime.
        </p>
      </div>

      {/* Create key */}
      <form onSubmit={createKey} className="paper-card p-5 flex flex-col sm:flex-row sm:items-end gap-4 max-w-2xl" data-testid="create-key-form">
        <div className="flex-1">
          <label className="field-label">Key name</label>
          <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} required maxLength={200}
            placeholder="e.g. Sean's laptop" data-testid="key-name-input" />
        </div>
        <label className="flex items-center gap-2 font-sans text-sm text-ink pb-2.5 cursor-pointer">
          <input type="checkbox" checked={readOnly} onChange={(e) => setReadOnly(e.target.checked)} data-testid="key-readonly-checkbox" />
          Read-only
        </label>
        <button type="submit" className="btn-brass" disabled={busy} data-testid="create-key-button">
          <Plus size={14} /> Create key
        </button>
      </form>

      {/* Key shown once */}
      <Modal open={!!created} onClose={() => setCreated(null)} title="Your new API key" testid="key-created-modal"
        footer={
          <button onClick={() => setCreated(null)} className="btn-outline text-sm" data-testid="key-modal-done">Done</button>
        }>
        {created && (
          <div className="space-y-4">
            <p className="font-mono text-sm text-ink break-all border border-hairline bg-paper p-3" data-testid="key-once-value">{created.key}</p>
            <div className="flex items-center gap-3">
              <button onClick={() => copyText(created.key, "Key")} className="btn-brass text-sm" data-testid="key-copy-button">
                <Copy size={14} /> Copy
              </button>
              <p className="font-sans text-sm text-garnet">You won't see this key again.</p>
            </div>
          </div>
        )}
      </Modal>

      {/* Keys table */}
      <div className="paper-card p-0 overflow-x-auto" data-testid="keys-table">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-hairline">
              {["Name", "Prefix", "Owner", "Created", "Last used", "Status", ""].map((h) => (
                <th key={h} className="overline px-4 py-3 font-normal">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k.id} className="border-b border-hairline last:border-0" data-testid={`key-row-${k.id}`}>
                <td className="px-4 py-3 font-sans text-sm text-ink">{k.name}</td>
                <td className="px-4 py-3 font-mono text-xs text-stone">{k.prefix}…</td>
                <td className="px-4 py-3 font-sans text-sm text-stone">{k.owner_email}</td>
                <td className="px-4 py-3 font-sans text-xs text-stone">{fmt(k.created_at)}</td>
                <td className="px-4 py-3 font-sans text-xs text-stone">{fmt(k.last_used_at)}</td>
                <td className="px-4 py-3 font-sans text-xs">
                  {k.revoked_at
                    ? <span className="text-garnet">Revoked</span>
                    : <span className="text-ink">Active{k.scope === "read" ? " · read-only" : ""}</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  {!k.revoked_at && (
                    <button onClick={() => setRevoking(k)} className="btn-ghost text-sm text-garnet" data-testid={`revoke-key-${k.id}`}>
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {keys.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 font-sans text-sm text-stone">
                <KeyRound size={14} className="inline mr-2 text-brass" />No keys yet — create one above.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal open={!!revoking} onClose={() => setRevoking(null)} title="Revoke this key?" testid="revoke-key-modal"
        footer={
          <>
            <button onClick={() => setRevoking(null)} className="btn-ghost text-sm" data-testid="revoke-cancel">Cancel</button>
            <button onClick={revoke} className="btn-brass text-sm" data-testid="revoke-confirm">Revoke</button>
          </>
        }>
        <p className="font-sans text-sm text-ink/80">
          {revoking ? `"${revoking.name}" stops working immediately. The row stays here for the record.` : ""}
        </p>
      </Modal>

      {/* Connect your tools */}
      <div className="paper-card p-5" data-testid="connect-tools-box">
        <div className="flex items-start justify-between gap-4">
          <pre className="font-mono text-xs text-ink/85 whitespace-pre-wrap leading-relaxed flex-1">{CONNECT_TEXT}</pre>
          <button onClick={() => copyText(CONNECT_TEXT, "Instructions")} className="btn-outline text-sm shrink-0" data-testid="copy-connect-text">
            <Copy size={14} /> Copy
          </button>
        </div>
      </div>

      {/* API reference */}
      <div className="space-y-4" data-testid="api-reference">
        <h2 className="font-serif text-2xl text-ink">API reference</h2>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => copyText(JSON.stringify(spec, null, 2), "Schema")} className="btn-outline text-sm" disabled={!spec} data-testid="copy-schema-button">
            <Copy size={14} /> Copy full schema JSON
          </button>
          <button onClick={downloadSpec} className="btn-outline text-sm" disabled={!spec} data-testid="download-spec-button">
            <Download size={14} /> Download API spec
          </button>
        </div>
        <div className="paper-card p-0 divide-y divide-hairline">
          {endpoints.map((e) => (
            <div key={`${e.method} ${e.path}`} className="px-4 py-3">
              <p className="font-mono text-xs text-ink">
                <span className="text-brass font-bold">{e.method}</span> {e.path}
                {e.summary && <span className="text-stone"> — {e.summary}</span>}
              </p>
              <p className="font-mono text-[11px] text-stone mt-1 break-all">
                curl {e.method !== "GET" ? `-X ${e.method} ` : ""}-H "Authorization: Bearer wa_YOUR_KEY" {serverUrl}{e.path}
              </p>
            </div>
          ))}
          {!spec && <p className="px-4 py-6 font-sans text-sm text-stone">Loading the spec…</p>}
        </div>
      </div>

      {/* Recent activity */}
      <div className="space-y-4" data-testid="recent-activity">
        <h2 className="font-serif text-2xl text-ink">Recent activity</h2>
        <div className="paper-card p-0 overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-hairline">
                {["When", "Who", "Via", "Action", "Detail"].map((h) => (
                  <th key={h} className="overline px-4 py-3 font-normal">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {audit.map((r) => (
                <tr key={r.id} className="border-b border-hairline last:border-0">
                  <td className="px-4 py-2.5 font-sans text-xs text-stone whitespace-nowrap">{fmt(r.at)}</td>
                  <td className="px-4 py-2.5 font-sans text-xs text-ink">{r.actor_email}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-stone">{r.auth_method}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-ink">{r.action}</td>
                  <td className="px-4 py-2.5 font-sans text-xs text-stone break-all">{r.detail || r.target_id || ""}</td>
                </tr>
              ))}
              {audit.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 font-sans text-sm text-stone">Nothing recorded yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
